import type { RuntimeConfig } from '../config';
import { APIError } from './LobbyService';
import { isLocalForceEvent } from './localForceEvent';
import { gameUpdateCode, gameUpdateMessage, physicsProtocols, physicsRevision } from './physicsRevision';
import { readPredictionState } from './predictionState';
import { decodeRestraint } from './restraintState';
import { ownerMotionChanged } from './ownerMotionPriority';
import { RealtimeAuthorityWatchdog } from './RealtimeAuthorityWatchdog';
import type {
  ClientInput,
  Credentials,
  Lobby,
  MatchEvent,
  NetworkPlayer,
  ServerMessage,
  Snapshot,
} from './protocol';

export type ConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'closed';
type OutgoingMessage = ClientInput | { type: 'ready'; ready: boolean }
  | { type: 'pickup' | 'swap_weapon'; targetId: string }
  | { type: 'swap_item'; targetId: string; slot: number }
  | { type: 'extract' | 'advance' | 'coupler' | 'primary' }
  | { type: 'item'; slot: number } | { type: 'chat'; body: string };
const snapshotBurstWindowMs = 6;
const maximumSnapshotDeliveryWaitMs = 40;
const maximumPendingEvents = 64;

export interface RealtimeEvents {
  onState(state: ConnectionState): void;
  onLobby(lobby: Lobby): void;
  onSnapshot(snapshot: Snapshot): void;
  onSnapshotDelivery?(diagnostics: SnapshotDeliveryDiagnostics): void;
  onError(message: string): void;
  onSessionExpired?(authorization: string): void;
  onUpdateRequired?(): void;
}

export interface SnapshotDeliveryDiagnostics {
  received: number;
  applied: number;
  coalesced: number;
  maximumBatch: number;
  discardedEvents: number;
}

export interface RealtimeSocketGrant {
  authorization: string;
  grant: string | null;
}

export type RealtimeGrantProvider = () => Promise<RealtimeSocketGrant>;

export class RealtimeClient {
  private readonly authorityWatchdog = new RealtimeAuthorityWatchdog(() => {
    if (this.socket) this.retireSocket(this.socket, 'authoritative updates timed out');
  });
  private socket: WebSocket | null = null;
  private reconnectTimer = 0;
  private attempts = 0;
  private disposed = false;
  private pendingReady: boolean | null = null;
  private connecting = false;
  private pendingSnapshot: Snapshot | null = null;
  private readonly pendingSnapshotEvents = new Map<number, MatchEvent>();
  private readonly deliveredForceIDs = new Set<number>();
  private snapshotTimer = 0;
  private snapshotFrame = 0;
  private snapshotFrameFallback = 0;
  private snapshotDeadline = 0;
  private snapshotBatch = 0;
  private receivedSnapshots = 0;
  private appliedSnapshots = 0;
  private coalescedSnapshots = 0;
  private maximumSnapshotBatch = 0;
  private discardedSnapshotEvents = 0;
  private appliedLife = 0;
  private appliedOwner: NetworkPlayer | null = null;
  private appliedSnapshotTick = -1;
  private appliedSnapshotTime = -Infinity;
  private latestSentInput: Readonly<ClientInput> | null = null;

  public constructor(
    private readonly config: RuntimeConfig,
    private readonly credentials: Credentials,
    private readonly events: RealtimeEvents,
    private readonly grantProvider: RealtimeGrantProvider = () => Promise.resolve({ authorization: '', grant: null }),
  ) {}

  public connect(): void {
    if (this.disposed || this.connecting || this.socket?.readyState === WebSocket.OPEN || this.socket?.readyState === WebSocket.CONNECTING) return;
    this.connecting = true;
    this.events.onState(this.attempts === 0 ? 'connecting' : 'reconnecting');
    void this.openSocket();
  }

  private async openSocket(): Promise<void> {
    let socketGrant: RealtimeSocketGrant;
    try {
      socketGrant = await this.grantProvider();
    } catch (error) {
      this.connecting = false;
      if (this.disposed) return;
      if (error instanceof APIError && error.code === gameUpdateCode) {
        this.stopForUpdate();
        return;
      }
      this.events.onError(error instanceof Error ? error.message : 'The boarding grant failed.');
      this.scheduleReconnect();
      return;
    }
    this.connecting = false;
    if (this.disposed) return;
    const url = new URL('/api/v1/ws', this.config.websocketOrigin);
    const authorization = [
      'scrapline-auth',
      this.credentials.lobbyCode,
      this.credentials.playerId,
      this.credentials.ticket,
    ].join('.');
    const protocols = socketGrant.grant
      ? ['scrapline-v2', 'scrapline-v1', ...physicsProtocols, authorization, 'scrapline-grant.' + socketGrant.grant]
      : ['scrapline-v1', ...physicsProtocols, authorization];
    const socket = new WebSocket(url, protocols);
    this.socket = socket;
    socket.addEventListener('open', () => {
      if (socket !== this.socket) return;
      this.attempts = 0;
      this.authorityWatchdog.start();
      this.events.onState('connected');
      const pendingReady = this.pendingReady;
      if (pendingReady !== null) {
        this.pendingReady = null;
        if (!this.send({ type: 'ready', ready: pendingReady })) this.pendingReady = pendingReady;
      }
    });
    socket.addEventListener('message', (event) => {
      if (socket !== this.socket || typeof event.data !== 'string') return;
      this.receive(event.data);
    });
    socket.addEventListener('close', (event) => {
      if (socket !== this.socket) return;
      this.socket = null;
      this.authorityWatchdog.stop();
      this.latestSentInput = null;
      this.clearPendingSnapshot();
      if (event.code === 4401) this.events.onSessionExpired?.(socketGrant.authorization);
      if (!this.disposed) this.scheduleReconnect();
    });
    socket.addEventListener('error', () => {
      this.retireSocket(socket, 'transport failed');
    });
  }

  public ready(ready: boolean): void {
    // Lobby readiness is durable user intent, unlike a weapon/gadget action.
    // A fast joiner can click during the short CONNECTING window; retain only
    // their latest choice and flush it as soon as that socket opens.
    this.pendingReady = ready;
    if (this.send({ type: 'ready', ready })) this.pendingReady = null;
  }

  public input(input: ClientInput): number | null {
    // Only successfully transmitted intent can supersede an accepted control.
    // Keep our own copy: callers may reuse their outgoing input object.
    const sentAt = this.transmit(input);
    if (sentAt !== null) this.latestSentInput = { ...input };
    return sentAt;
  }

  public pickup(targetId: string): void {
    this.send({ type: 'pickup', targetId });
  }

  public swapWeapon(targetId: string): void {
    this.send({ type: 'swap_weapon', targetId });
  }

  public swapItem(targetId: string, slot: number): void {
    this.send({ type: 'swap_item', targetId, slot });
  }

  public extract(): void {
    this.send({ type: 'extract' });
  }

  public advance(): void {
    this.send({ type: 'advance' });
  }

  public item(slot: number, seq?: number): boolean {
    return this.send({ type: 'item', slot, ...(seq === undefined ? {} : { seq }) });
  }

  public coupler(): void {
    this.send({ type: 'coupler' });
  }

  public primary(): void {
    this.send({ type: 'primary' });
  }

  public chat(body: string): void {
    this.send({ type: 'chat', body });
  }

  public dispose(): void {
    this.disposed = true;
    this.connecting = false;
    this.pendingReady = null;
    this.authorityWatchdog.stop();
    this.clearPendingSnapshot();
    window.clearTimeout(this.reconnectTimer);
    this.socket?.close(1000, 'leaving lobby');
    this.socket = null;
    this.events.onState('closed');
  }

  private receive(raw: string): void {
    try {
      const message = JSON.parse(raw) as ServerMessage;
      if (message.type === 'lobby' || message.type === 'hello') {
        if (message.lobby?.phase === 'waiting') this.deliveredForceIDs.clear();
        if (message.lobby) this.events.onLobby(message.lobby);
      } else if (message.type === 'snapshot' && message.snapshot) {
        if (physicsRevision(message.snapshot.physicsRevision) === null) {
          this.stopForUpdate();
          return;
        }
        const owner = message.ownerPrediction;
        for (const player of message.snapshot.players ?? []) {
          delete player.itemReceipt;
          delete player.restraint;
        }
        if (owner?.playerId === this.credentials.playerId) {
          const player = message.snapshot.players?.find(player => player.id === owner.playerId);
          if (player) {
            player.predictionState = owner.state;
            player.restraint = decodeRestraint(owner.restraint, player, message.snapshot.players);
            const receipt = owner.itemReceipt;
            if (receipt && Number.isInteger(receipt.seq) && receipt.seq >= 0 && receipt.seq <= player.lastInput
              && typeof receipt.accepted === 'boolean') player.itemReceipt = { ...receipt };
          }
        }
        // Legacy snapshots remain usable. An invalid optional replay baseline
        // is discarded without inventing replacement jump/climb/cable memory.
        for (const player of message.snapshot.players ?? []) {
          if (player.predictionState !== undefined && !readPredictionState(player)) {
            delete player.predictionState;
            delete player.itemReceipt;
            delete player.restraint;
          }
        }
        this.queueSnapshot(message.snapshot);
      } else if (message.type === 'error') {
        if (message.code === gameUpdateCode) {
          this.stopForUpdate();
          return;
        }
        this.events.onError(message.message ?? 'The match server rejected an action.');
      }
    } catch {
      this.events.onError('The match server sent an unreadable update.');
    }
  }

  /** Consume the newest already-delivered state before the game's render
   * callback. A separately queued rAF can otherwise run after that callback
   * and keep a known impact invisible for another whole low-FPS frame. */
  public flushBeforeRender(): void {
    if (this.pendingSnapshot) this.flushSnapshot();
  }

  private queueSnapshot(snapshot: Snapshot): void {
    const receivedAt = performance.now();
    this.receivedSnapshots += 1;
    this.snapshotBatch += 1;
    const latestTick = this.pendingSnapshot?.tick ?? this.appliedSnapshotTick;
    const latestTime = this.pendingSnapshot?.serverTime ?? this.appliedSnapshotTime;
    const stale = snapshot.tick < latestTick
      || snapshot.tick === latestTick && snapshot.serverTime < latestTime;
    if (!stale) {
      this.pendingSnapshot = snapshot;
    }
    // Compare the selected newest baseline, not an out-of-order packet that
    // cannot replace it. Known owner catches/releases and accepted controls
    // must not inherit a low-FPS animation queue's extra handoff delay.
    const owner = this.pendingSnapshot?.players.find(player => player.id === this.credentials.playerId);
    let urgent = ownerMotionChanged(this.appliedOwner, owner, this.latestSentInput);
    for (const event of snapshot.events ?? []) {
      this.pendingSnapshotEvents.set(event.id, event);
      while (this.pendingSnapshotEvents.size > maximumPendingEvents) {
        const oldest = this.pendingSnapshotEvents.keys().next().value;
        if (oldest === undefined) break;
        this.pendingSnapshotEvents.delete(oldest);
        this.discardedSnapshotEvents += 1;
      }
    }
    // Old states cannot rewind train/life/grip presentation after a newer
    // baseline was already applied. Keep their bounded transient events for
    // the next current state; an empty render flush must not discard them.
    if (!this.pendingSnapshot) return;
    if (!stale) this.authorityWatchdog.advance(snapshot.tick, snapshot.serverTime);
    for (const event of this.pendingSnapshotEvents.values()) {
      if (isLocalForceEvent(event, this.credentials.playerId) && !this.deliveredForceIDs.has(event.id)) {
        this.deliveredForceIDs.add(event.id);
        urgent = true;
        if (this.deliveredForceIDs.size > 128) this.deliveredForceIDs.delete(this.deliveredForceIDs.values().next().value!);
      }
    }
    // New local impacts and owner transitions start their presentation now.
    // The burst timer can be delayed by a whole low-FPS render; unchanged
    // motion and already-delivered duplicate events retain normal coalescing.
    if (urgent) { this.flushSnapshot(); return; }
    // On a saturated main thread the browser may keep dispatching queued
    // WebSocket tasks while starving both timers and animation frames. Do not
    // let that scheduling policy hold authority for whole seconds: once the
    // original delivery budget has elapsed, the newest message being handled
    // is already a safer state than the deferred backlog.
    if (this.snapshotDeadline > 0 && receivedAt >= this.snapshotDeadline) {
      this.flushSnapshot();
      return;
    }
    if (this.snapshotTimer === 0 && this.snapshotFrame === 0) {
      this.snapshotDeadline = receivedAt+maximumSnapshotDeliveryWaitMs;
      // A small fixed window catches TCP/browser delivery bursts without
      // delaying responsive local prediction. Apply only the newest complete
      // state on the next paint and retain the bounded transient event stream.
      this.snapshotTimer = window.setTimeout(() => {
        this.snapshotTimer = 0;
        if (!this.disposed && this.pendingSnapshot) {
          this.snapshotFrame = requestAnimationFrame(this.flushSnapshot);
          // requestAnimationFrame can fall to a few frames per second on a
          // saturated or transitioning renderer. Authority ingestion must not
          // inherit that cadence: a bounded watchdog advances to the latest
          // state even when the next paint is abnormally late.
          this.snapshotFrameFallback = window.setTimeout(
            this.flushSnapshot,
            maximumSnapshotDeliveryWaitMs-snapshotBurstWindowMs,
          );
        }
      }, snapshotBurstWindowMs);
    }
  }

  private stopForUpdate(): void {
    this.dispose();
    if (this.events.onUpdateRequired) this.events.onUpdateRequired();
    else this.events.onError(gameUpdateMessage);
  }

  private readonly flushSnapshot = (): void => {
    window.clearTimeout(this.snapshotTimer);
    this.snapshotTimer = 0;
    if (this.snapshotFrame !== 0) cancelAnimationFrame(this.snapshotFrame);
    this.snapshotFrame = 0;
    window.clearTimeout(this.snapshotFrameFallback);
    this.snapshotFrameFallback = 0;
    this.snapshotDeadline = 0;
    const snapshot = this.pendingSnapshot;
    const batch = this.snapshotBatch;
    this.pendingSnapshot = null;
    this.snapshotBatch = 0;
    if (this.disposed || !snapshot) {
      this.pendingSnapshotEvents.clear();
      return;
    }
    const events = [...this.pendingSnapshotEvents.values()].sort((left, right) => left.id-right.id);
    this.pendingSnapshotEvents.clear();
    this.appliedSnapshots += 1;
    this.coalescedSnapshots += Math.max(0, batch-1);
    this.maximumSnapshotBatch = Math.max(this.maximumSnapshotBatch, batch);
    const owner = snapshot.players.find(player => player.id === this.credentials.playerId);
    if (owner && Number.isSafeInteger(owner.falls) && owner.falls >= this.appliedLife) this.appliedLife = owner.falls;
    this.appliedOwner = owner ?? null;
    this.appliedSnapshotTick = snapshot.tick;
    this.appliedSnapshotTime = snapshot.serverTime;
    // Apply the life boundary before callbacks: Game can immediately send the
    // neutral recovery input while handling this same authoritative snapshot.
    this.events.onSnapshot(events.length > 0 ? { ...snapshot, events } : snapshot);
    this.events.onSnapshotDelivery?.({
      received: this.receivedSnapshots,
      applied: this.appliedSnapshots,
      coalesced: this.coalescedSnapshots,
      maximumBatch: this.maximumSnapshotBatch,
      discardedEvents: this.discardedSnapshotEvents,
    });
  };

  private clearPendingSnapshot(): void {
    this.deliveredForceIDs.clear();
    window.clearTimeout(this.snapshotTimer);
    this.snapshotTimer = 0;
    if (this.snapshotFrame !== 0) cancelAnimationFrame(this.snapshotFrame);
    this.snapshotFrame = 0;
    window.clearTimeout(this.snapshotFrameFallback);
    this.snapshotFrameFallback = 0;
    this.snapshotDeadline = 0;
    this.pendingSnapshot = null;
    this.pendingSnapshotEvents.clear();
    this.snapshotBatch = 0;
  }

  private send(message: OutgoingMessage): boolean {
    return this.transmit(message) !== null;
  }

  /** Successful native send clock, after serialization and before transport.
   * Zero is a valid clock; null means no command was transmitted. */
  private transmit(message: OutgoingMessage): number | null {
    if (this.socket?.readyState !== WebSocket.OPEN) return null;
    // First life is implicitly zero. Tag all later gameplay intent, not just
    // movement, so old buffered attacks/items cannot act through a respawn.
    const scoped = this.appliedLife > 0 && message.type !== 'ready' && message.type !== 'chat'
      ? { ...message, life: this.appliedLife } : message;
    const serialized = JSON.stringify(scoped);
    const sentAt = performance.now();
    this.socket.send(serialized);
    return sentAt;
  }

  private scheduleReconnect(): void {
    this.attempts += 1;
    this.events.onState('reconnecting');
    const delay = Math.min(8_000, 400 * 2 ** Math.min(this.attempts - 1, 5));
    window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = window.setTimeout(() => this.connect(), delay);
  }

  private retireSocket(socket: WebSocket, reason: string): void {
    if (this.disposed || socket !== this.socket) return;
    // Detach before close: a blackholed close handshake can itself stall.
    // Late messages/close events from this transport cannot renew authority,
    // alter the new seat or schedule a second reconnect.
    this.socket = null;
    this.authorityWatchdog.stop();
    this.latestSentInput = null;
    this.clearPendingSnapshot();
    this.scheduleReconnect();
    socket.close(4000, reason);
  }
}
